<template>
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label="Resize Atlas AI panel"
    tabindex="0"
    :class="[
      'absolute top-0 left-0 h-full w-1.5 z-10 cursor-col-resize select-none',
      'group',
      dragging ? 'bg-blue-500/40' : isDark ? 'hover:bg-blue-500/30' : 'hover:bg-blue-500/20'
    ]"
    @pointerdown="onPointerDown"
    @keydown="onKeyDown"
    @dblclick="onDoubleClick"
  >
    <div
      :class="[
        'absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-0.5 h-8 rounded-full transition-colors',
        dragging
          ? 'bg-blue-400'
          : isDark
            ? 'bg-gray-600 group-hover:bg-blue-400'
            : 'bg-gray-400 group-hover:bg-blue-500'
      ]"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, inject, computed } from 'vue'
import { useTheme } from '../../utils/useTheme'
import {
  useAiChatPanelStore,
  PANEL_WIDTH_DEFAULT,
  PANEL_WIDTH_MIN,
  PANEL_WIDTH_MAX
} from '../../stores/ai-chat-panel.store'

export default defineComponent({
  name: 'AiChatPanelResizer',
  setup() {
    const theme = inject('theme', useTheme())
    const isDark = computed(() => theme.isDark.value)
    const panel = useAiChatPanelStore()
    const dragging = ref(false)

    let startX = 0
    let startWidth = 0
    let activePointerId = -1
    let bodyUserSelect = ''

    function onPointerMove(e: PointerEvent) {
      if (!dragging.value || e.pointerId !== activePointerId) return
      // Panel sits flush with the right edge → moving pointer LEFT increases width.
      const dx = startX - e.clientX
      panel.setWidth(startWidth + dx)
    }

    function onPointerUp(e: PointerEvent) {
      if (e.pointerId !== activePointerId) return
      dragging.value = false
      activePointerId = -1
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      if (typeof document !== 'undefined') {
        document.body.style.userSelect = bodyUserSelect
      }
    }

    function onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return
      e.preventDefault()
      dragging.value = true
      startX = e.clientX
      startWidth = panel.widthPx
      activePointerId = e.pointerId
      bodyUserSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', onPointerMove)
      window.addEventListener('pointerup', onPointerUp)
      window.addEventListener('pointercancel', onPointerUp)
    }

    function onKeyDown(e: KeyboardEvent) {
      const step = e.shiftKey ? 40 : 10
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        panel.setWidth(panel.widthPx + step)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        panel.setWidth(panel.widthPx - step)
      } else if (e.key === 'Home') {
        e.preventDefault()
        panel.setWidth(PANEL_WIDTH_MAX)
      } else if (e.key === 'End') {
        e.preventDefault()
        panel.setWidth(PANEL_WIDTH_MIN)
      }
    }

    function onDoubleClick() {
      panel.setWidth(PANEL_WIDTH_DEFAULT)
    }

    return { dragging, isDark, onPointerDown, onKeyDown, onDoubleClick }
  }
})
</script>
